// Graphic 2 of 4 — failure recurrence, family × week. Rows come pre-ordered by
// volume from the API (families[]), so a burst reads as one hot row, not 40
// charts. Rendered as a div grid (the board's own idiom) — no new chart
// library, and the table view is the accessible twin of the colour ramp.
import { useState } from 'react';
import { seriesOne } from '../../lib/palette';
import { weekLabel, heatmapModel, heatStep } from '../../lib/trends';
import ChartCard from '../ChartCard';

// Opacity steps of the single validated series hue — intensity IS the value,
// so one hue with 4 visible steps, never a multi-hue ramp.
const STEP_OPACITY = [0, 0.16, 0.38, 0.64, 1];

export default function RecurrenceHeatmap({ analytics, mode }) {
  const [showTable, setShowTable] = useState(false);
  const hue = seriesOne(mode);
  const m = heatmapModel(analytics);
  // The chart caps rows for legibility; the table is the complete record.
  const full = heatmapModel(analytics, { maxRows: Infinity });
  const weeks = analytics.series;

  if (m.rows.length === 0) {
    return (
      <ChartCard title="Failure recurrence" subtitle="family × week">
        <p className="chart-empty">No failures recorded in this window.</p>
      </ChartCard>
    );
  }

  const subtitle = m.dimension === 'family'
    ? 'failure family × week · rows ordered by volume'
    // Honest fallback: the fixture store carries no family annotations.
    : 'failure category × week (this store records no families) · rows ordered by volume';

  return (
    <ChartCard
      title="Failure recurrence"
      subtitle={subtitle}
      showTable={showTable}
      onToggleTable={() => setShowTable((v) => !v)}
      table={
        <table className="data-table">
          <thead>
            <tr>
              <th>{m.dimension}</th>
              {weeks.map((w) => <th key={w.week_start} className="num">{weekLabel(w.week_start)}</th>)}
            </tr>
          </thead>
          <tbody>
            {full.rows.map((r) => (
              <tr key={r.key}>
                <td><code>{r.key}</code></td>
                {r.cells.map((v, i) => <td key={weeks[i].week_start} className="num">{v || '—'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <div className="heat" role="img" aria-label={`failure recurrence by ${m.dimension} and week`}>
        {m.rows.map((r) => (
          <div key={r.key} className="heat__row">
            <span className="heat__label" title={r.key}><code>{r.key}</code></span>
            {r.cells.map((v, i) => {
              const step = heatStep(v, m.max);
              return (
                <span
                  key={weeks[i].week_start}
                  className="heat__cell"
                  style={step ? { background: hue, opacity: STEP_OPACITY[step] } : undefined}
                  title={`${r.key} · week of ${weekLabel(weeks[i].week_start)} · ${v} failure${v === 1 ? '' : 's'}`}
                />
              );
            })}
          </div>
        ))}
        <div className="heat__row heat__row--axis" aria-hidden="true">
          <span className="heat__label" />
          {weeks.map((w) => <span key={w.week_start} className="heat__tick">{weekLabel(w.week_start)}</span>)}
        </div>
      </div>
      {m.truncated > 0 && (
        <p className="chart-note">{m.truncated} smaller {m.dimension === 'family' ? 'families' : 'categories'} not shown — the table view lists every value.</p>
      )}
    </ChartCard>
  );
}
